// Project → Invoice tab. Hits /projects/:id/invoices which lists every
// invoice tied to one of the project's orders. Read-only list in the design
// table grammar (other Business UI handoff, 2026-10-08); the invoice record
// itself carries send / payment / credit actions.
//
// Balance due is the server's `outstanding` (balance_due minus ledgered
// payments) — rendered, never computed here.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/ui/StatusPill";
import { TABLE_HEAD, TABLE_WRAP, TD, TH, TROW } from "@/components/ui/RecordParts";
import { cn } from "@/lib/utils";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

interface InvoiceRow {
  id: string;
  invoiceNumber: number;
  orderId: string;
  orderNumber: number;
  invoiceType: "deposit" | "final";
  invoiceDate: string;
  status: string;
  total: string;
  balanceDue: string | null;
  /** What is still OWED — balance_due minus ledgered payments (server-computed). */
  outstanding: string;
  paidAt: string | null;
  createdAt: string | null;
}

interface InvoicesListResponse {
  rows: InvoiceRow[];
}

function statusTone(
  s: string,
): "active" | "prospect" | "dormant" | "closed" | "warn" | "info" {
  switch (s) {
    case "open":
    case "sent_to_client":
    case "exported":
      return "info";
    case "paid":
      return "active";
    case "voided":
      return "closed";
    default:
      return "info";
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case "open":
      return "Not sent";
    case "sent_to_client":
      return "Sent";
    case "exported":
      return "Exported";
    case "paid":
      return "Paid";
    case "voided":
      return "Voided";
    default:
      return s.replace(/_/g, " ");
  }
}

function formatMoney(s: string | null): string {
  if (s === null) return "—";
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function formatDate(s: string | null): string {
  if (!s) return "—";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return new Date(s + "T00:00:00").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function ProjectInvoiceTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const navigate = useNavigate();
  const projectId = data.project.id;

  const { data: list, isLoading, isError } = useQuery({
    queryKey: ["project", projectId, "invoices"],
    queryFn: () =>
      api<InvoicesListResponse>(`/projects/${projectId}/invoices`),
  });

  const rows = list?.rows ?? [];
  // Row-grounded sales-order count (rail node) — an invoice is created by
  // converting the Quote, so the empty state points there when one exists.
  const salesOrderCount =
    data.stages.find((s) => s.key === "sales_order")?.count ?? 0;

  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Invoices</h2>
        {!isLoading && !isError && (
          <p className="text-xs text-muted">
            {rows.length} {rows.length === 1 ? "invoice" : "invoices"}
          </p>
        )}
      </div>

      {isLoading ? (
        <div className={`${TABLE_WRAP} space-y-3 p-5`} aria-label="Loading invoices">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-4 w-full animate-pulse rounded bg-canvas" />
          ))}
        </div>
      ) : isError ? (
        <div role="alert" className="rounded-md border border-risk/30 bg-risk-bg p-5 text-sm text-risk">
          We couldn't load this project's invoices. Nothing has changed — refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        salesOrderCount > 0 ? (
          // Stage-aware: a Quote exists, so the real action (Convert to
          // Invoice) lives on the SO detail surface — point the rep there
          // in-context rather than leaving a read-only dead-end.
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
            <p className="font-medium text-ink">No invoices yet.</p>
            <p className="mt-1 text-sm text-muted">
              Invoices are created by converting the quote.{" "}
              <Link
                to={`/projects/${projectId}/quotes`}
                className="font-medium text-ink underline underline-offset-4"
              >
                Open the quote
              </Link>
            </p>
          </div>
        ) : (
          // No Quote yet — honest explain state (nothing to convert).
          <div className="rounded-md border border-rule bg-white px-5 py-12 text-center">
            <p className="font-medium text-ink">No invoices yet.</p>
            <p className="mt-1 text-sm text-muted">
              Invoices are created from a quote once the estimate is
              approved and converted.
            </p>
          </div>
        )
      ) : (
        <div className={TABLE_WRAP} role="region" aria-label="Project invoices" tabIndex={0}>
          <table className="w-full text-left">
            <thead className={TABLE_HEAD}>
              <tr>
                <th scope="col" className={TH}>Invoice</th>
                <th scope="col" className={TH}>Status</th>
                <th scope="col" className={`${TH} hidden sm:table-cell`}>Date</th>
                <th scope="col" className={`${TH} hidden text-right md:table-cell`}>Total</th>
                <th scope="col" className={`${TH} text-right`}>Balance due</th>
              </tr>
            </thead>
            <tbody className="text-[13px]">
              {rows.map((i) => (
                <tr
                  key={i.id}
                  onClick={() => navigate(`/projects/${projectId}/invoice/${i.id}`)}
                  className={cn(TROW, "cursor-pointer")}
                >
                  <td className={TD}>
                    <Link
                      to={`/projects/${projectId}/invoice/${i.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-medium text-ink hover:underline"
                    >
                      Invoice #{i.invoiceNumber}
                    </Link>
                    <span className="block text-[11px] text-muted">
                      {i.invoiceType === "deposit" ? "Invoice · Prepay" : "Invoice"} · Order #{i.orderNumber}
                      <span className="sm:hidden"> · {formatDate(i.invoiceDate)}</span>
                    </span>
                  </td>
                  <td className={TD}>
                    <StatusPill className="normal-case" tone={statusTone(i.status)}>{statusLabel(i.status)}</StatusPill>
                  </td>
                  <td className={`${TD} hidden text-ink sm:table-cell`}>
                    {formatDate(i.invoiceDate)}
                  </td>
                  <td className={`${TD} hidden text-right tabular-nums text-muted md:table-cell`}>
                    {formatMoney(i.total)}
                  </td>
                  <td className={`${TD} text-right font-medium tabular-nums text-ink`}>
                    {formatMoney(i.outstanding ?? i.balanceDue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
